/**
 * View-model "Dự kiến": gom dự chi, dự thu, dự trả thẻ/nợ (ba collection riêng) và các khoản
 * "ảo" của mẫu định kỳ chưa được sinh thành MỘT danh sách cho một tháng. Thuần hàm, có test.
 * Không ghi gì — mọi lệnh ghi đi qua ledger/plans.
 */
import { daysOverdue, followedAmount, previousPeriod } from '../../../finance';
import { toDate } from '../transactions/entries';

export type PlannedKind = 'expense' | 'income' | 'payment';
export type PlannedStatus = 'pending' | 'confirmed' | 'cancelled';

export interface PlannedEntry {
  key: string;
  id: string;
  kind: PlannedKind;
  status: PlannedStatus;
  date: Date | null;
  /** Luôn dương; chiều thu/chi do `kind` quyết định */
  amount: number;
  title: string;
  detail: string;
  walletName: string;
  category?: string;
  recurring: boolean;
  templateId?: string;
  /** Khoản của mẫu định kỳ chưa được sinh ra (tháng sau) — chỉ để xem, chưa xác nhận được */
  virtual: boolean;
  /** Số tiền lấy theo khoản đã xác nhận của kỳ trước (mẫu "theo số tháng trước") */
  followed?: boolean;
  /** Số tiền trả thẻ/nợ định kỳ chỉ là ước tính tới khi người dùng nhập số thật */
  estimate: boolean;
  daysLate: number;
  cancelReason?: string;
  doc: any;
}

export interface PlannedSource {
  plannedExpenses: any[]; plannedIncomes: any[]; plannedPayments: any[]; recurringTemplates: any[];
}

const ymOf = (y?: number, m?: number) => (y ?? 0) * 12 + (m ?? 0);
const keyOf = (y: number, m: number) => `${y}-${String(m).padStart(2, '0')}`;
const sod = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

const paymentTitle = (p: any) => (p.paymentType === 'debt' ? `Trả nợ ${p.debtName ?? ''}` : `Trả thẻ ${p.creditCardName ?? ''}`).trim();

const fromDoc = (kind: PlannedKind, d: any, today: Date): PlannedEntry => {
  const date = toDate(d.plannedDate);
  const pending = d.status === 'pending';
  const note = kind === 'income' ? d.description || d.notes : d.note;
  return {
    key: `${kind}:${d.id}`, id: d.id, kind, status: d.status as PlannedStatus, date,
    amount: Number(d.amount) || 0,
    title: kind === 'payment' ? paymentTitle(d) : d.category || d.categoryName || 'Khác',
    detail: note || '',
    walletName: (kind === 'payment' ? d.sourceWalletName : d.walletName) || '',
    category: kind === 'payment' ? undefined : d.category || d.categoryName,
    recurring: !!d.isRecurring, templateId: d.templateId, virtual: false, followed: !!d.amountFollowed,
    estimate: kind === 'payment' && pending && !!d.isRecurring,
    daysLate: pending && date ? Math.max(0, daysOverdue(date, today)) : 0,
    cancelReason: d.cancelReason, doc: d
  };
};

/**
 * Các khoản dự kiến của THÁNG xem (year, month):
 * - mọi khoản thuộc tháng đó (mọi trạng thái);
 * - nếu đang xem tháng hiện tại: thêm khoản CÒN CHỜ của các tháng trước (quá hạn);
 * - nếu xem tháng hiện tại hoặc sau: thêm khoản "ảo" của mẫu định kỳ đang bật mà chưa được sinh.
 */
export const plannedForMonth = (src: PlannedSource, year: number, month: number, now: Date = new Date()): PlannedEntry[] => {
  const today = sod(now);
  const viewYm = ymOf(year, month);
  const curYm = ymOf(now.getFullYear(), now.getMonth() + 1);
  const out: PlannedEntry[] = [];

  const take = (kind: PlannedKind, docs: any[]) => {
    for (const d of docs) {
      const ym = ymOf(d.year, d.month);
      if (ym === viewYm || (viewYm === curYm && d.status === 'pending' && ym < viewYm)) out.push(fromDoc(kind, d, today));
    }
  };
  take('expense', src.plannedExpenses); take('income', src.plannedIncomes); take('payment', src.plannedPayments);

  if (viewYm >= curYm) {
    const existing = new Set<string>([...src.plannedExpenses, ...src.plannedIncomes, ...src.plannedPayments].map(d => d.id));
    const key = keyOf(year, month);
    const dim = new Date(year, month, 0).getDate();
    for (const t of src.recurringTemplates) {
      if (!t.active || !(Number(t.amount) > 0)) continue;
      if (t.startMonth && key < t.startMonth) continue;
      if (t.frequency === 'yearly' && (Number(t.monthOfYear) || 1) !== month) continue;
      if (existing.has(`${t.id}_${key}`)) continue;
      const day = Math.min(Math.max(1, Number(t.dayOfMonth) || 1), dim);
      const kind: PlannedKind = t.type === 'income' ? 'income' : t.type === 'payment' ? 'payment' : 'expense';
      // Mẫu theo số tháng trước: kỳ trước đã xác nhận thì lấy số đó (khớp với số sẽ được tạo)
      const pp = previousPeriod(t, year, month);
      const prevPool = kind === 'income' ? src.plannedIncomes : kind === 'payment' ? src.plannedPayments : src.plannedExpenses;
      const followed = followedAmount(t, prevPool.find(d => d.id === `${t.id}_${keyOf(pp.year, pp.month)}`));
      out.push({
        key: `virtual:${t.id}:${key}`, id: `${t.id}_${key}`, kind, status: 'pending', date: new Date(year, month - 1, day),
        amount: followed ?? Number(t.amount), followed: followed !== null, title: t.type === 'payment' ? t.name : t.category, detail: t.type === 'payment' ? '' : t.name,
        walletName: t.walletName || '', category: t.type === 'payment' ? undefined : t.category,
        recurring: true, templateId: t.id, virtual: true, estimate: kind === 'payment', daysLate: 0, doc: t
      });
    }
  }
  return out.sort((a, b) => (a.date?.getTime() ?? 0) - (b.date?.getTime() ?? 0) || a.key.localeCompare(b.key));
};

/** Khoản dự kiến của nhiều tháng (quý/năm): gộp từng tháng, bỏ trùng (khoản quá hạn chỉ kéo vào tháng hiện tại). */
export const plannedForMonths = (src: PlannedSource, months: Array<{ y: number; m: number }>, now: Date = new Date()): PlannedEntry[] => {
  const seen = new Map<string, PlannedEntry>();
  for (const { y, m } of months) for (const e of plannedForMonth(src, y, m, now)) if (!seen.has(e.key)) seen.set(e.key, e);
  return [...seen.values()].sort((a, b) => (a.date?.getTime() ?? 0) - (b.date?.getTime() ?? 0) || a.key.localeCompare(b.key));
};

export interface PlannedGroups { late: PlannedEntry[]; upcoming: PlannedEntry[]; later: PlannedEntry[]; done: PlannedEntry[]; skipped: PlannedEntry[] }

/** Chia nhóm: quá hạn · sắp tới (đã sinh, chờ xác nhận) · sẽ sinh sau (ảo) · đã xác nhận · đã bỏ qua. */
export const groupPlanned = (entries: PlannedEntry[]): PlannedGroups => ({
  late: entries.filter(e => e.status === 'pending' && !e.virtual && e.daysLate > 0),
  upcoming: entries.filter(e => e.status === 'pending' && !e.virtual && e.daysLate === 0),
  later: entries.filter(e => e.virtual),
  done: entries.filter(e => e.status === 'confirmed'),
  skipped: entries.filter(e => e.status === 'cancelled')
});

export interface PlannedSummary {
  income: number; expense: number; payment: number; net: number;
  /** Tổng chi = dự chi + trả thẻ & nợ; đã xác nhận; còn chờ xác nhận */
  spend: number; spendDone: number; spendLeft: number;
  /** Tiến độ: đã xác nhận / tổng khoản của tháng (không tính đã bỏ qua) */
  confirmed: number; total: number; lateCount: number; late60Count: number;
}

/** Tổng hợp THÁNG xem: chỉ các khoản thuộc tháng đó (không tính khoản quá hạn kéo từ tháng trước), bỏ khoản đã bỏ qua. */
export const summarizePlanned = (entries: PlannedEntry[], year: number, month: number): PlannedSummary =>
  summarizePlannedIn(entries, [{ y: year, m: month }]);

/** Như summarizePlanned nhưng cho một tập tháng (quý/năm). */
export const summarizePlannedIn = (entries: PlannedEntry[], months: Array<{ y: number; m: number }>): PlannedSummary => {
  const set = new Set(months.map(x => `${x.y}-${x.m}`));
  const inMonth = entries.filter(e => e.status !== 'cancelled' && e.date && set.has(`${e.date.getFullYear()}-${e.date.getMonth() + 1}`));
  const sum = (k: PlannedKind) => inMonth.filter(e => e.kind === k).reduce((s, e) => s + e.amount, 0);
  const income = sum('income'), expense = sum('expense'), payment = sum('payment');
  const outs = inMonth.filter(e => e.kind !== 'income');
  const spendDone = outs.filter(e => e.status === 'confirmed').reduce((s, e) => s + e.amount, 0);
  const real = inMonth.filter(e => !e.virtual);
  const lateAll = entries.filter(e => e.status === 'pending' && !e.virtual && e.daysLate > 0);
  return {
    income, expense, payment, net: income - expense - payment,
    spend: expense + payment, spendDone, spendLeft: expense + payment - spendDone,
    confirmed: real.filter(e => e.status === 'confirmed').length, total: real.length,
    lateCount: lateAll.length, late60Count: lateAll.filter(e => e.daysLate > 60).length
  };
};

/** Số khoản đang chờ cần xử lý ngay (đã sinh): dùng cho huy hiệu trên menu và Tổng quan. */
export const pendingDueCount = (src: PlannedSource, now: Date = new Date()): { today: number; late: number } => {
  const entries = plannedForMonth(src, now.getFullYear(), now.getMonth() + 1, now).filter(e => e.status === 'pending' && !e.virtual);
  return { today: entries.filter(e => e.daysLate === 0).length, late: entries.filter(e => e.daysLate > 0).length };
};

/** Lệch giữa số thực tế và số dự kiến (để gợi ý cập nhật mẫu khi lệch từ ngưỡng, mặc định 10%). */
export const variance = (planned: number, actual: number, threshold = 0.1): { diff: number; pct: number; significant: boolean } => {
  const diff = actual - planned;
  const pct = planned > 0 ? diff / planned : 0;
  return { diff, pct, significant: planned > 0 && Math.abs(pct) >= threshold };
};

export interface DuplicatePayment { key: string; label: string; entries: PlannedEntry[]; total: number }
/** Cùng một thẻ/khoản vay có từ 2 khoản trả đang chờ (kể cả khoản sắp sinh từ mẫu) trong cùng một tháng: dễ trả hai lần. */
export const duplicatePayments = (entries: PlannedEntry[]): DuplicatePayment[] => {
  const groups = new Map<string, PlannedEntry[]>();
  for (const e of entries) {
    if (e.kind !== 'payment' || e.status !== 'pending' || !e.date) continue;
    const d = e.doc ?? {};
    const target = d.paymentType === 'debt' ? `debt:${d.debtId}` : `card:${d.creditCardId}`;
    if (!d.debtId && !d.creditCardId) continue;
    const k = `${target}|${e.date.getFullYear()}-${e.date.getMonth() + 1}`;
    groups.set(k, [...(groups.get(k) ?? []), e]);
  }
  return [...groups.entries()].filter(([, v]) => v.length >= 2).map(([key, v]) => ({
    key, label: v[0].title, entries: v, total: v.reduce((s, x) => s + x.amount, 0)
  }));
};

/** Khoản đang chờ chia theo độ gấp: trong 7 ngày tới (kể cả hôm nay) và phần còn lại của kỳ. */
export const splitUpcoming = (items: PlannedEntry[], now: Date = new Date()): { soon: PlannedEntry[]; rest: PlannedEntry[] } => {
  const limit = sod(now).getTime() + 7 * 86_400_000;
  const soon: PlannedEntry[] = [], rest: PlannedEntry[] = [];
  for (const e of items) (e.date && e.date.getTime() < limit ? soon : rest).push(e);
  return { soon, rest };
};

/** Tiền ròng của một nhóm: thu trừ chi và trả thẻ/nợ (âm = ra nhiều hơn vào). */
export const groupNet = (items: PlannedEntry[]): number => items.reduce((s, e) => s + (e.kind === 'income' ? e.amount : -e.amount), 0);
